import { yupResolver } from '@hookform/resolvers/yup';
import { Close } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { sanitizeCpfOrName } from 'utils/sanitizeCpfOrName';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import {
  setAllSellerList,
  setSellerInvalidRowsModalOpen,
  setUploadSellerFileResponse,
} from 'v3/data/store/seller/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import { listAllSellers, uploadSellerCreationFile } from 'v3/services/api';
import { IUploadSellerCreationFileResponse } from 'v3/services/interfaces/response';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { TextButton } from 'v3/shared/components/Button/TextButton';
import { RadioFormController } from 'v3/shared/components/Inputs/RadioFormController';

import {
  AlignButtons,
  CloseBox,
  CloseIconStyled,
  Content,
  FileContainer,
  StoreListContainer,
  StyledDialog,
} from 'v3/shared/components/Modal/UploadModal/styles';
import { IUploadModalProps } from 'v3/shared/components/Modal/UploadModal/types';
import { sellerUploadFileModalSchema } from 'v3/shared/schemas/sellerUploadFileModalSchema';
import { BodyMediumRegular } from 'v3/shared/typography/BodyMedium';
import { HeadlineSmall } from 'v3/shared/typography/HeadlineSmall';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';
import { TitleSmall } from 'v3/shared/typography/TitleSmall';

const MINIMUM_NUMBER_OF_VALIDS_ROWS = 1;
const ONE_MEGA_BYTES_IN_BYTES = 1024 * 1024;
const FIRST_ACCEPTED_FILE_INDEX = 0;
const MAXIMUM_FILE_SIZE = '1MB';

export const UploadModal: React.FC<IUploadModalProps> = ({ open, setOpenModal }) => {
  const theme = useTheme();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const { sellersFilter } = useStoreSelector((store) => store.seller);
  const { storeList } = useStoreSelector((store) => store.seller);

  const { page, rowsPerPage } = sellersFilter;

  const getStoreOptions = (stores: Array<{ id: string; name: string }>) =>
    Object.fromEntries(stores.map(({ id, name }) => [name, id]));

  const {
    control,
    getValues,
    reset,
    formState: { isValid },
  } = useForm<{ storeId: string }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(sellerUploadFileModalSchema) as any,
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    maxSize: ONE_MEGA_BYTES_IN_BYTES,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        setErrorMessage(`Arquivo inválido ou maior que ${MAXIMUM_FILE_SIZE}`);
        setUploadedFile(null);
        return;
      }
      setErrorMessage(null);
      setUploadedFile(acceptedFiles[FIRST_ACCEPTED_FILE_INDEX]);
    },
  });

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setErrorMessage('Selecione um arquivo antes de enviar.');
  };

  const handleClose = () => {
    setOpenModal(false);
    setUploadedFile(null);
    reset();
  };

  const handleUploadFile = async () => {
    if (!uploadedFile) {
      setErrorMessage('Selecione um arquivo antes de enviar.');
      return;
    }
    const { storeId } = getValues();

    try {
      const response = await uploadSellerCreationFile(storeId, uploadedFile);
      setUploadSellerFileResponse(response.data);
      handleUploadResponse(response.data);

      await fetchSellerList();
    } catch (error) {
      showErrorDialog('Erro no upload do arquivo', error);
    }
  };

  const fetchSellerList = async () => {
    try {
      const response = await listAllSellers({
        rowsPerPage,
        page,
        searchString: sanitizeCpfOrName(sellersFilter.filter),
        active: sellersFilter.active,
        storeIds: sellersFilter.storeIds,
      });

      setAllSellerList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista dos consultores', error);
    }
  };

  const handleUploadResponse = (response: IUploadSellerCreationFileResponse) => {
    handleClose();
    if (response.validRowsCount < MINIMUM_NUMBER_OF_VALIDS_ROWS) {
      return showSnackbar(
        'Todos os dados estão com inconsistências, por favor revise e envie novamente.',
        'error',
        true,
      );
    }
    if (response.invalidRowsCount > 0 && response.validRowsCount >= 0) {
      return setSellerInvalidRowsModalOpen(true);
    }

    showSnackbar('Arquivo enviado com sucesso', 'success', true);
  };
  return (
    <StyledDialog open={open}>
      <Content>
        <CloseBox>
          <CloseIconStyled onClick={handleClose} aria-label="Fechar modal" />
        </CloseBox>

        <HeadlineSmall color={theme.palette.common.white}>Enviar template</HeadlineSmall>

        <BodyMediumRegular color={theme.palette.common.white}>1 Envie o template</BodyMediumRegular>

        {uploadedFile ? (
          <FileContainer hasFile={!!uploadedFile}>
            <SubtitleMedium color={theme.palette.secondary.main}>
              📄 {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)
            </SubtitleMedium>
            <IconButton onClick={handleRemoveFile} size="small">
              <Close color="error" />
            </IconButton>
          </FileContainer>
        ) : (
          <FileContainer hasFile={!!uploadedFile} {...getRootProps({ className: 'dropzone' })}>
            <input {...getInputProps()} />
            <BodyMediumRegular>Arraste o template aqui</BodyMediumRegular>
            <TitleSmall>Ou</TitleSmall>
            <TextButton text="Selecione o template" size="small" $outline />
          </FileContainer>
        )}

        {errorMessage && <SubtitleMedium color={theme.palette.tertiary.main}>{errorMessage}</SubtitleMedium>}

        <BodyMediumRegular color={theme.palette.common.white}>2 Escolha a loja</BodyMediumRegular>
        <StoreListContainer>
          <RadioFormController
            formControl={control}
            formControlName="storeId"
            label=""
            defaultValues={getStoreOptions(storeList)}
            disabled={false}
          />
        </StoreListContainer>

        <SubtitleMedium color={theme.palette.secondary.main}>
          *Arquivo suportado: XLS, XLSX, CSV (máx. {MAXIMUM_FILE_SIZE})
        </SubtitleMedium>

        <AlignButtons>
          <FlexButton
            text="Salvar"
            size="small"
            disabled={!isValid || !uploadedFile}
            handleClick={handleUploadFile}
            fullWidth
          />
        </AlignButtons>
      </Content>
    </StyledDialog>
  );
};
